/**
 * Resize + compress an image client-side.
 *
 * Defaults to WebP @ ~0.78 quality (typically 25-35% smaller than JPEG @ 0.8 at
 * equivalent visual quality), with an automatic JPEG fallback for browsers that
 * cannot encode WebP via canvas (very rare in 2026).
 */
export async function compressImage(
  source: File | Blob,
  opts: { maxDim?: number; quality?: number } = {}
): Promise<{ blob: Blob; ext: "webp" | "jpg"; contentType: string }> {
  const maxDim = opts.maxDim ?? 1200;
  const quality = opts.quality ?? 0.78;

  const blob: Blob = await new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("No 2D canvas context"));
      return;
    }
    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      if (w > maxDim || h > maxDim) {
        if (w > h) {
          h = (h / w) * maxDim;
          w = maxDim;
        } else {
          w = (w / h) * maxDim;
          h = maxDim;
        }
      }
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(img, 0, 0, w, h);

      // Try WebP first
      canvas.toBlob(
        (out) => {
          if (out && out.type === "image/webp") {
            resolve(out);
            return;
          }
          // Fallback: JPEG
          canvas.toBlob(
            (jpg) => (jpg ? resolve(jpg) : reject(new Error("toBlob failed"))),
            "image/jpeg",
            0.8
          );
        },
        "image/webp",
        quality
      );
    };
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = URL.createObjectURL(source);
  });

  if (blob.type === "image/webp") {
    return { blob, ext: "webp", contentType: "image/webp" };
  }
  return { blob, ext: "jpg", contentType: "image/jpeg" };
}
